import {
  Box,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Portal,
  Text,
  Th,
  VisuallyHidden,
  useDisclosure,
  type TableColumnHeaderProps,
} from "@chakra-ui/react";
import { Tooltip } from "../components/Tooltip";
import { t } from "i18next";
import { useId, useRef, useState } from "react";
import type { IconType } from "react-icons";
import {
  FaArrowDown,
  FaArrowsAltV,
  FaArrowUp,
  FaFilter,
  FaSearch,
} from "react-icons/fa";
import { SortDirection } from "../../autogen/bff-api";
import {
  activeFilterParts,
  chosenFilterValueCount,
  hasValueFilter,
} from "./columnFilters";
import { ColumnSearchEditor, ColumnValueEditor } from "./ColumnEditors";
import { EntityTableColumn } from "./EntityTable";
import {
  focusFirstControl,
  fromTheMenuToItsFirstControl,
  useFocusIntoMenuOnEveryOpening,
} from "./menuFocus";

export const ColumnHeader = ({
  column,
  debounceMs = 300,
  onDropdownOpen,
  onDropdownClose,
  thProps,
}: {
  column: EntityTableColumn;
  debounceMs?: number;
  onDropdownOpen?: (dropdownKey: string) => void;
  onDropdownClose?: (dropdownKey: string) => void;
  thProps?: TableColumnHeaderProps;
}) => {
  const searchList = useRef<HTMLDivElement>(null);
  const searchMenu = useDisclosure();
  const filterMenu = useDisclosure();
  const filterList = useRef<HTMLDivElement>(null);
  const focusIntoSearch = useFocusIntoMenuOnEveryOpening(searchList);
  const focusIntoFilter = useFocusIntoMenuOnEveryOpening(filterList);
  const [filterClosings, setFilterClosings] = useState(0);
  const countId = useId();

  const handleSortToggle = () => {
    if (!column.onSort) return;
    if (!column.currentSortDirection) return column.onSort(SortDirection.Asc);
    if (column.currentSortDirection === SortDirection.Asc)
      return column.onSort(SortDirection.Desc);
    return column.onSort(undefined as unknown as SortDirection);
  };

  const hasInteractiveFeatures =
    column.onSearch || column.onSort || hasValueFilter(column);
  const searchIsActive = activeFilterParts(column).includes("search");
  const searchCount = searchIsActive ? 1 : 0;
  const valueCount = chosenFilterValueCount(column) - searchCount;
  const searchBadge = hasValueFilter(column) ? 0 : searchCount;
  const shownCount = valueCount || searchBadge;
  const valueFilterIsActive =
    typeof column.isFilterActive === "boolean"
      ? column.isFilterActive
      : valueCount > 0;

  const getSortIcon = () => {
    if (column.currentSortDirection === SortDirection.Asc) return FaArrowUp;
    if (column.currentSortDirection === SortDirection.Desc) return FaArrowDown;
    return FaArrowsAltV;
  };

  return (
    <Th
      key={column.key}
      width={column.width}
      minWidth={column.width}
      maxWidth={column.width}
      py="0"
      {...thProps}
    >
      {hasInteractiveFeatures ? (
        <Flex
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          px={2}
          py={1}
          _hover={{ bg: "smBackgroundSecondary" }}
          borderRadius="sm"
        >
          <Text data-column-label>{column.label}</Text>

          <Flex alignItems="center" gap={1}>
            {shownCount > 0 && (
              <Portal>
                <VisuallyHidden id={countId}>
                  {t("{{count}} chosen", { count: shownCount })}
                </VisuallyHidden>
              </Portal>
            )}
            {column.onSearch && (
              <Menu
                isOpen={searchMenu.isOpen}
                onOpen={() => {
                  searchMenu.onOpen();
                  onDropdownOpen?.(`${column.key}-search`);
                  focusIntoSearch.start();
                }}
                onClose={() => {
                  focusIntoSearch.stop();
                  searchMenu.onClose();
                  onDropdownClose?.(`${column.key}-search`);
                }}
              >
                <Tooltip
                  label={
                    t("Search in {{column}}", { column: column.label }) ?? ""
                  }
                >
                  <Box as="span" display="inline-flex">
                    <MenuButton
                      as={IconButton}
                      aria-label={
                        t("Search in {{column}}", { column: column.label }) ??
                        ""
                      }
                      icon={<FilterIcon icon={FaSearch} count={searchBadge} />}
                      aria-describedby={searchBadge ? countId : undefined}
                      size="xs"
                      variant="ghost"
                      color={searchIsActive ? "smPrimary" : "smMuted"}
                    />
                  </Box>
                </Tooltip>
                <Portal>
                  <MenuList
                    ref={searchList}
                    maxHeight="96"
                    overflowY="auto"
                    onFocus={focusFirstControl}
                    onKeyDown={fromTheMenuToItsFirstControl}
                  >
                    <ColumnSearchEditor column={column} onClose={searchMenu.onClose} />
                  </MenuList>
                </Portal>
              </Menu>
            )}

            {hasValueFilter(column) && (
              <Menu
                isOpen={filterMenu.isOpen}
                onOpen={() => {
                  filterMenu.onOpen();
                  onDropdownOpen?.(`${column.key}-filter`);
                  focusIntoFilter.start();
                }}
                onClose={() => {
                  focusIntoFilter.stop();
                  filterMenu.onClose();
                  setFilterClosings((closings) => closings + 1);
                  onDropdownClose?.(`${column.key}-filter`);
                }}
              >
                <Tooltip
                  label={t("Filter {{column}}", { column: column.label }) ?? ""}
                >
                  <Box as="span" display="inline-flex">
                    <MenuButton
                      as={IconButton}
                      aria-label={
                        t("Filter {{column}}", { column: column.label }) ?? ""
                      }
                      icon={<FilterIcon icon={FaFilter} count={valueCount} />}
                      aria-describedby={valueCount ? countId : undefined}
                      size="xs"
                      variant="ghost"
                      color={valueFilterIsActive ? "smPrimary" : "smMuted"}
                    />
                  </Box>
                </Tooltip>
                <Portal>
                  <MenuList
                    ref={filterList}
                    maxHeight="96"
                    overflowY="auto"
                    onFocus={focusFirstControl}
                    onKeyDown={fromTheMenuToItsFirstControl}
                  >
                    <ColumnValueEditor
                      closings={filterClosings}
                      column={column}
                      debounceMs={debounceMs}
                      onClose={filterMenu.onClose}
                    />
                  </MenuList>
                </Portal>
              </Menu>
            )}
            {column.onSort && (
              <Tooltip
                label={t("Sort {{column}}", { column: column.label }) ?? ""}
              >
                <IconButton
                  aria-label={
                    t("Sort {{column}}", { column: column.label }) ?? ""
                  }
                  icon={<Icon as={getSortIcon()} />}
                  size="xs"
                  variant="ghost"
                  color={column.currentSortDirection ? "smPrimary" : "smMuted"}
                  onClick={handleSortToggle}
                />
              </Tooltip>
            )}
          </Flex>
        </Flex>
      ) : (
        <Box p={2}>
          <Flex alignItems="center" gap={1}>
            {column.label}
          </Flex>
        </Box>
      )}
    </Th>
  );
};

const FilterIcon = ({ icon, count }: { icon: IconType; count: number }) =>
  count === 0 ? (
    <Icon as={icon} />
  ) : (
    <Flex alignItems="center" gap="0.5">
      <Icon as={icon} />
      <Text as="span" fontSize="xs" fontWeight="bold">
        {count}
      </Text>
    </Flex>
  );
